import { Enrollment } from '@clear-denver/tpc-openapi-typescript'
import { getXCLEARCorrelationInfo } from '@clear-denver/tpc-openapi-typescript/build/utils/get-xclear-correlation-info'
import {
  DiscreteEvent,
  dispatchCustomEvent,
  EventName,
  KPI,
} from '@utils/customEvents'

const PREFIX = '[PNEL]'

/** only to be used for PNEL */
export const createPNELTelemetryPipe = (debugEnabled: boolean) => {
  let isDebugging = debugEnabled

  const setDebug = (enabled: boolean) => {
    isDebugging = enabled
  }

  const emit = (
    message: string,
    event: DiscreteEvent,
    meta: Omit<Record<string, string | number | boolean>, 'level' | 'route'> & {
      route: string
      level?: keyof typeof Enrollment.LogEntryLevelEnum
    }
  ) => {
    const xCLEARCorrelationInfo = getXCLEARCorrelationInfo()
    const baseMetaData = {
      kpi: KPI.USER_INTERACTION,
      xCLEARCorrelationInfo,
    }

    const args = [
      meta.level as EventName,
      {
        payload: {
          message,
        },
        metadata: {
          ...baseMetaData,
          ...meta,
          event,
        },
      },
    ] as const
    if (isDebugging) {
      console.log(PREFIX + '[DEBUG]', ...args)
    }
    dispatchCustomEvent(...args)
  }

  const action = (
    meta: Parameters<typeof emit>[2] & { type: 'START' | 'SUBMIT' }
  ) => {
    meta.level ??= 'INFO'

    const { type } = meta
    if (type === 'SUBMIT') {
      emit(
        `${PREFIX} Payment Submitted`,
        DiscreteEvent.PAYMENT_SUBMIT_BEGIN,
        meta
      )
      return
    }

    if (type === 'START') {
      emit(`${PREFIX} Payment Shown`, DiscreteEvent.PAYMENT_VIEW, {
        ...meta,
        isShown: true,
      })
      return
    }

    throw new Error(
      `${PREFIX} cannot emit action - the type '${type}' is not supported`
    )
  }

  /**
   * * note: the `disabled` SKIPPED reason is overloaded at this time
   */
  const conclude = (
    meta:
      | (Parameters<typeof emit>[2] & { type: 'SUCCESS' })
      | (Parameters<typeof emit>[2] & {
          reason: 'disabled' | 'user-selected'
          type: 'SKIPPED'
        })
  ) => {
    meta.level ??= 'INFO'

    const { type } = meta
    if (type === 'SUCCESS') {
      emit(
        '[PNEL] Payment Succeeded',
        DiscreteEvent.PAYMENT_SUBMIT_SUCCESSFUL,
        meta
      )
      return
    }

    if (type === 'SKIPPED') {
      const { reason } = meta
      if (reason === 'disabled') {
        emit(
          '[PNEL] Feature Disabled in Flow',
          DiscreteEvent.PAYMENT_SKIPPED,
          meta
        )
        return
      }

      if (reason === 'user-selected') {
        emit('[PNEL] Payment Opted Out', DiscreteEvent.PAYMENT_SKIPPED, meta)
        return
      }

      throw new Error(
        `${PREFIX} cannot emit conclusion - the reason '${reason}' (${type}) is not supported`
      )
    }

    throw new Error(
      `${PREFIX} cannot emit conclusion - the type '${type}' is not supported`
    )
  }

  /**
   * TODO: update reporting api to distinguish between terminal and recoverable error paths
   */
  const failure = (
    meta: Parameters<typeof emit>[2] & {
      type: 'API' | 'FORM'
      errMsg: Error['message']
    }
  ) => {
    meta.level ??= 'ERROR'

    emit(
      `${PREFIX} Payment Failure: ${meta.errMsg}`,
      meta.type === 'API'
        ? DiscreteEvent.PAYMENT_SUBMIT_FAILURE
        : DiscreteEvent.PAYMENT_FORM_FAILURE,
      meta
    )
  }

  return {
    setDebug,
    action,
    failure,
    conclude,
  } as const
}
